import { useMemo } from 'react'
import {
    NyuData,
    NyuDataPointsWithParsedTime,
    NyuGraphData,
    NyuSeriesProperties,
} from '../../../../shared/types'
import useFilterYears from '../../hooks/useFilterYears'
import useGlobalState from '../../hooks/useGlobalState'
import { canActivateHighContrastMode } from '../../hooks/useHighContrastMode'
import useToggleState from '../../hooks/useToggleState'
import {
    getGroupsFromSeriesProperties,
    parseTimeValue,
} from '../../utils/helpers'
import CustomChart from '../CustomChart'
import DownloadDataButton from '../DownloadDataButton'
import Dropdown from '../Dropdown'
import HighContrastToggle from '../Graph/HighContrastMode/HighContrastToggle'
import HeaderLogos from '../HeaderLogos'
import SwitchButton from '../SwitchButton'

export default function PageSwitch({ data }: { data: NyuData }) {
    const {
        meta: { title },
        dataset,
        uiProperties,
    } = data

    const downloadView = useGlobalState((state) => state.downloadView)

    // TODO: Expecting dataset to be not nested, potentially unsafe
    const { dataProperties, seriesProperties } = dataset as NyuGraphData

    const dataPointsWithParsedTime: NyuDataPointsWithParsedTime =
        useMemo(() => {
            const unchunkedDataset = dataset as NyuGraphData
            return unchunkedDataset.data.map(parseTimeValue)
        }, [dataset])

    const { dataPoints, startYearDropdownProps, endYearDropdownProps } =
        useFilterYears(dataPointsWithParsedTime, dataProperties)

    const groups = useMemo(() => {
        return getGroupsFromSeriesProperties(
            seriesProperties as NyuSeriesProperties
        )
    }, [seriesProperties])

    // active groups
    const allGroupIds = groups.map(({ id }) => id)
    const [activeGroupIds, toggleActiveGroupId] = useToggleState(
        new Set(allGroupIds)
    )
    const activeGroups = groups.filter(({ id }) => activeGroupIds.has(id))

    const hideAll = dataProperties.hideControls?.includes('all')
    const hideDownloadButton =
        downloadView ||
        dataProperties.hideControls?.includes('download') ||
        hideAll

    const hideHighContrastToggle =
        downloadView ||
        !canActivateHighContrastMode(dataProperties, seriesProperties) ||
        dataProperties.hideControls?.includes('highContrast')

    const hideFilter =
        dataProperties.hideControls?.includes('filter') ||
        hideAll ||
        downloadView

    const yearOptionsValid =
        startYearDropdownProps.options.length > 1 ||
        endYearDropdownProps.options.length > 1
    const showYearFilter = !hideFilter && yearOptionsValid

    return (
        <div data-testid="chart-section">
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="text-xl font-bold" data-testid="headline">
                    {title}
                </div>
                {downloadView && (
                    <div className="text-right font-bold">
                        DHL Globalization Tracker
                    </div>
                )}
            </div>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                {downloadView && (
                    <div className="mt-1 inline-flex gap-1 whitespace-nowrap md:mt-0">
                        {groups.map((group, index) => (
                            <div
                                key={`${group}_${index}_download`}
                                className="group"
                            >
                                <span className="text-gray-500">
                                    {group.firstLine.label}
                                </span>
                                <span className="text-gray-500 group-last:hidden">
                                    ,
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {!downloadView && (
                    <div className="grid grid-cols-1 gap-4 xs:grid-cols-2 lg:grid-cols-3">
                        {groups.map((group, index) => (
                            <SwitchButton
                                key={`${index}_${group.id}`}
                                id={`${index}_${group.id}`}
                                label={group.firstLine.label}
                                checked={activeGroupIds.has(group.id)}
                                disabled={
                                    activeGroupIds.size === 1 &&
                                    activeGroupIds.has(group.id)
                                }
                                onCheckedChange={() =>
                                    toggleActiveGroupId(group.id)
                                }
                                checkedColor={group.firstLine.color}
                            />
                        ))}
                    </div>
                )}

                <div className="mb-2 ml-auto flex flex-wrap items-start justify-between gap-2">
                    {showYearFilter && (
                        <>
                            <Dropdown
                                label="Start Year"
                                className="ml-auto w-20 text-left"
                                testId="start-year-dropdown"
                                {...startYearDropdownProps}
                            />
                            <Dropdown
                                label="End Year"
                                className="w-20 text-left"
                                testId="end-year-dropdown"
                                {...endYearDropdownProps}
                            />
                        </>
                    )}

                    {!hideHighContrastToggle && <HighContrastToggle />}

                    {!hideDownloadButton && (
                        <DownloadDataButton
                            title={title}
                            dataPoints={dataPoints}
                            dataProperties={dataProperties}
                            seriesProperties={seriesProperties}
                            pullRight={!showYearFilter}
                            activeDataPointKeys={Array.from(activeGroupIds)}
                            cite={data.meta.cite}
                        />
                    )}
                </div>

                <HeaderLogos />
            </div>
            <CustomChart
                dataPoints={dataPoints}
                dataProperties={dataProperties}
                seriesProperties={seriesProperties}
                uiProperties={uiProperties}
                groups={activeGroups}
            />
        </div>
    )
}
